import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { WebsiteEnquiry, WebsiteEnquiryStatus } from '@contracts/interfaces/website-enquiry.interface';
import { WebsiteEnquiryService } from '../../services/website-enquiry.service';
import { MatDialog } from '@angular/material/dialog';
import { ApiDocsDialogComponent } from '../../dialogs/api-docs/api-docs.dialog';
import { WebsiteEnquiryDetailDialogComponent } from '../../dialogs/website-enquiry-detail/website-enquiry-detail.dialog';
import { forkJoin, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomerFactory } from '@contracts/factories/customer.factory';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { Task, TaskStatus } from '@contracts/interfaces/task.interface';
import { CustomerService } from 'src/app/services/customer.service';
import { TaskService } from 'src/app/services/task.service';

interface WebsiteEnquiryInfo {
  newCount: number;
  archivedCount: number;
  latestUpdatedAt?: Date | string | null;
}

@Component({
  selector: 'app-website-enquiries',
  templateUrl: './website-enquiries.component.html',
  styleUrls: ['./website-enquiries.component.scss', '../table.scss'],
})
export class WebsiteEnquiriesComponent implements OnInit, OnDestroy {

  WebsiteEnquiryStatus = WebsiteEnquiryStatus;
  activeTab = "new";
  isLoading = false;

  newCount = 0;
  archivedCount = 0;

  displayedColumns: string[] = [
    'date',
    'enquiry',
    'service',
    'spam',
    'message',
    'action'
  ];

  enquiries: WebsiteEnquiry[] = [];
  dataSource: MatTableDataSource<WebsiteEnquiry>;
  searchQuery = "";
  lastServerState = "";
  taskCreationInProgress = new Set<number>();
  private destroy$ = new Subject<void>();

  constructor(
    private websiteEnquiryService: WebsiteEnquiryService,
    private customerService: CustomerService,
    private taskService: TaskService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadEnquiries(true);
    this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  activateTab(tab: string) {
    this.activeTab = tab;
    this.loadEnquiries(true);
  }

  loadEnquiries(showSpinner = false) {
    this.isLoading = showSpinner;
    const status = this.activeTab === 'archived'
      ? WebsiteEnquiryStatus.Archived
      : WebsiteEnquiryStatus.New;

    forkJoin({
      info: this.websiteEnquiryService.getInfo(),
      enquiries: this.websiteEnquiryService.find({ status })
    }).subscribe(({ info, enquiries }: { info: WebsiteEnquiryInfo; enquiries: WebsiteEnquiry[] }) => {
      this.applyInfo(info);
      this.enquiries = (enquiries || []).sort((a, b) => {
        const ad = new Date(a?.dateCreated || 0).getTime();
        const bd = new Date(b?.dateCreated || 0).getTime();
        return bd - ad;
      });
      this.dataSource = new MatTableDataSource<WebsiteEnquiry>(this.enquiries);
      this.applySearchFilter();
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  archive(enquiry: WebsiteEnquiry, event?: Event) {
    event?.stopPropagation();
    enquiry.status = WebsiteEnquiryStatus.Archived;
    enquiry.dateArchived = new Date();
    this.websiteEnquiryService.update(enquiry).subscribe(() => this.loadEnquiries(false));
  }

  restore(enquiry: WebsiteEnquiry, event?: Event) {
    event?.stopPropagation();
    enquiry.status = WebsiteEnquiryStatus.New;
    delete enquiry.dateArchived;
    this.websiteEnquiryService.update(enquiry).subscribe(() => this.loadEnquiries(false));
  }

  makeTask(enquiry: WebsiteEnquiry, event?: Event) {
    event?.stopPropagation();
    if (!enquiry?.id || this.taskCreationInProgress.has(enquiry.id)) {
      return;
    }

    this.taskCreationInProgress.add(enquiry.id);
    const customer = this.buildCustomerFromEnquiry(enquiry);

    this.customerService.createOne(customer).subscribe({
      next: (response: any) => {
        const createdCustomer = CustomerFactory.validate(response?.customer || customer);
        const task = this.buildTaskFromEnquiry(enquiry, createdCustomer);

        this.taskService.create(task).subscribe({
          next: (taskResponse: any) => {
            const taskId = taskResponse?.id;
            enquiry.status = WebsiteEnquiryStatus.Archived;
            enquiry.dateArchived = new Date();
            (enquiry as any).taskId = taskId;

            this.websiteEnquiryService.update(enquiry).subscribe({
              next: () => {
                this.taskCreationInProgress.delete(enquiry.id);
                this.loadEnquiries(false);
                this.openCreatedTask(taskId, { ...task, id: taskId });
              },
              error: () => {
                this.taskCreationInProgress.delete(enquiry.id);
                this.cdr.detectChanges();
              }
            });
          },
          error: () => {
            this.taskCreationInProgress.delete(enquiry.id);
            this.cdr.detectChanges();
          }
        });
      },
      error: () => {
        this.taskCreationInProgress.delete(enquiry.id);
        this.cdr.detectChanges();
      }
    });
  }

  search(query: string) {
    this.searchQuery = query || "";
    this.applySearchFilter();
  }

  searchCancelled() {
    this.searchQuery = "";
    this.applySearchFilter();
  }

  getContact(enquiry: WebsiteEnquiry): string {
    return enquiry.name || 'No name';
  }

  getMessagePreview(enquiry: WebsiteEnquiry): string {
    const message = enquiry?.message || '';
    return message.length > 150 ? `${message.slice(0, 150).trim()}...` : message;
  }

  getEmailHref(enquiry: WebsiteEnquiry): string {
    return `mailto:${enquiry.email || ''}`;
  }

  getRawSubmittedData(enquiry: WebsiteEnquiry): string {
    if (!enquiry.rawSubmittedData) {
      return "";
    }

    return JSON.stringify(enquiry.rawSubmittedData, null, 2);
  }

  getMayBeSpam(enquiry: WebsiteEnquiry): boolean | null {
    const rawSubmittedData = enquiry?.rawSubmittedData;
    if (!rawSubmittedData || typeof rawSubmittedData !== "object") {
      return null;
    }

    const sources = [
      rawSubmittedData,
      rawSubmittedData.metadata,
      rawSubmittedData.metaData,
      rawSubmittedData.formData?.metadata,
      rawSubmittedData.data?.metadata
    ];

    for (const source of sources) {
      if (!source || typeof source !== "object" || source.mayBeSpam === undefined || source.mayBeSpam === null) {
        continue;
      }

      if (typeof source.mayBeSpam === "boolean") {
        return source.mayBeSpam;
      }

      if (typeof source.mayBeSpam === "string") {
        const value = source.mayBeSpam.trim().toLowerCase();
        if (value === "true") {
          return true;
        }
        if (value === "false") {
          return false;
        }
      }
    }

    return null;
  }

  isMakingTask(enquiry: WebsiteEnquiry): boolean {
    return !!enquiry?.id && this.taskCreationInProgress.has(enquiry.id);
  }

  get activeCount(): number {
    return this.activeTab === 'archived' ? this.archivedCount : this.newCount;
  }

  openApiDocs() {
    this.dialog.open(ApiDocsDialogComponent, {
      autoFocus: false,
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: {
        title: 'Website Enquiry Endpoints',
        services: ['website-enquiry-service']
      }
    });
  }

  openEnquiryDialog(enquiry: WebsiteEnquiry) {
    const ref = this.dialog.open(WebsiteEnquiryDetailDialogComponent, {
      autoFocus: false,
      width: '860px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: {
        enquiry,
        activeTab: this.activeTab,
        mayBeSpam: this.getMayBeSpam(enquiry),
        rawSubmittedData: this.getRawSubmittedData(enquiry),
        isMakingTask: this.isMakingTask(enquiry)
      }
    });

    ref.afterClosed().subscribe((result?: { action: 'make-task' | 'archive' | 'restore' }) => {
      if (!result?.action) {
        return;
      }

      if (result.action === 'make-task') {
        this.makeTask(enquiry);
      } else if (result.action === 'archive') {
        this.archive(enquiry);
      } else if (result.action === 'restore') {
        this.restore(enquiry);
      }
    });
  }

  private startLiveUpdates() {
    timer(10000, 10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkForServerUpdates());
  }

  private checkForServerUpdates() {
    if (this.isLoading) {
      return;
    }

    this.websiteEnquiryService.getInfo().subscribe((info: WebsiteEnquiryInfo) => {
      const incomingState = this.getServerState(info);
      if (this.lastServerState && incomingState !== this.lastServerState) {
        this.applyInfo(info);
        this.loadEnquiries(false);
        return;
      }

      this.applyInfo(info);
      this.cdr.detectChanges();
    });
  }

  private applyInfo(info: WebsiteEnquiryInfo) {
    this.newCount = info?.newCount || 0;
    this.archivedCount = info?.archivedCount || 0;
    this.lastServerState = this.getServerState(info);
  }

  private getServerState(info: WebsiteEnquiryInfo): string {
    return [
      info?.newCount || 0,
      info?.archivedCount || 0,
      info?.latestUpdatedAt || ''
    ].join('|');
  }

  private applySearchFilter() {
    if (this.dataSource) {
      this.dataSource.filter = this.searchQuery;
    }
  }

  private buildCustomerFromEnquiry(enquiry: WebsiteEnquiry): CustomerInterface {
    const customer = CustomerFactory.validate(CustomerFactory.newCustomer());
    customer.name = (enquiry.name || '').trim();
    customer.phone = (enquiry.phone || '').trim();
    customer.email = (enquiry.email || '').trim();
    customer.notes = [
      'Created from website enquiry.',
      enquiry.id ? `Enquiry ID: ${enquiry.id}` : '',
      enquiry.service ? `Service: ${enquiry.service}` : '',
      enquiry.postcode ? `Postcode: ${enquiry.postcode}` : '',
      enquiry.message ? `Message: ${enquiry.message}` : ''
    ].filter(v => !!v).join('\n');
    customer.tags = ['website-enquiry'];
    return customer;
  }

  private buildTaskFromEnquiry(enquiry: WebsiteEnquiry, customer: CustomerInterface): Task {
    const service = enquiry.service || 'Website enquiry';
    const name = enquiry.name || 'Website enquiry';

    return {
      id: 0,
      date: new Date(),
      type: 'website-enquiry',
      status: TaskStatus.InProgress,
      customer,
      subject: `${service} - ${name}`,
      description: [
        enquiry.message || 'Website enquiry',
        enquiry.service ? `Service: ${enquiry.service}` : '',
        enquiry.email ? `Email: ${enquiry.email}` : '',
        enquiry.phone ? `Phone: ${enquiry.phone}` : '',
        enquiry.postcode ? `Postcode: ${enquiry.postcode}` : '',
        enquiry.id ? `Website enquiry ID: ${enquiry.id}` : ''
      ].filter(line => !!line).join('\n'),
      dueDate: '',
      quote: 0,
      log: [
        {
          type: 'comment',
          text: 'Task created from website enquiry.',
          date: new Date()
        }
      ]
    };
  }

  private openCreatedTask(taskId: number, fallbackTask: Task) {
    if (!taskId) {
      this.taskService.openTaskDialog(fallbackTask, [fallbackTask.id], 0, () => this.loadEnquiries(false));
      return;
    }

    this.taskService.get(taskId).subscribe({
      next: (createdTask: Task) => {
        this.taskService.openTaskDialog(createdTask || fallbackTask, [taskId], 0, () => this.loadEnquiries(false));
      },
      error: () => {
        this.taskService.openTaskDialog(fallbackTask, [taskId], 0, () => this.loadEnquiries(false));
      }
    });
  }
}
